import { create } from 'zustand';
import type { ChatMessage } from '@contracts/messages';
import type { SessionIdentity } from '@contracts/session';
import type { VipProgress } from '@contracts/eligibility';
import type { NotifVipActivatedPayload } from '@contracts/ws-events';
import type { RateLimitedPayload } from '@contracts/moderation';
import type { UserBannedPayload } from '@contracts/bans';

export type ConnState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface TypingUser {
  userId: string;
  name: string;
  /** Wall-clock ms when this entry should auto-clear. */
  expiresAt: number;
}

export interface SystemEvent {
  /** Stable id used as React key. */
  id: string;
  kind: 'join' | 'leave';
  userId: string;
  name: string;
  /** ISO timestamp the event occurred (for chronological merging). */
  at: string;
}

interface ChatState {
  identity: SessionIdentity | null;
  messages: ChatMessage[];
  online: number;
  /** Map keyed by userId to dedupe across reconnect/typing-spam. */
  typing: Record<string, TypingUser>;
  /** Transient join/leave events shown inline in the chat. */
  systemEvents: SystemEvent[];
  connState: ConnState;
  errorMessage: string | null;
  /** Set when the server rejects with NOT_VIP. Drives the locked screen. */
  eligibility: { locked: boolean; progress: VipProgress | null };
  /** Active VIP-activated celebration popup (if any). */
  activation: NotifVipActivatedPayload | null;
  /** Active spam/rate-limit cooldown. untilAt is ISO; null when not cooling down. */
  cooldown: { untilAt: string; reason: 'rate-limit' | 'spam-escalation'; message: string } | null;
  /** Last block notice surfaced as a transient toast. */
  blockedToast: { reason: string; at: number } | null;
  /** Active ban affecting this user. Drives the full-screen ban notice. */
  ban: UserBannedPayload | null;
  /** Message the composer is currently quoting a reply to. */
  replyTarget: ChatMessage | null;

  setIdentity: (identity: SessionIdentity) => void;
  setHistory: (items: ChatMessage[]) => void;
  appendMessage: (msg: ChatMessage) => void;
  reconcileAck: (clientNonce: string, serverId: string, createdAt: string) => void;
  setOnline: (n: number) => void;
  addTyping: (userId: string, name: string, ttlMs?: number) => void;
  removeTyping: (userId: string) => void;
  pruneTyping: () => void;
  pushSystemEvent: (event: Omit<SystemEvent, 'id' | 'at'> & { at?: string }) => void;
  setEligibility: (locked: boolean, progress: VipProgress | null) => void;
  showActivation: (payload: NotifVipActivatedPayload) => void;
  dismissActivation: () => void;
  setCooldown: (payload: RateLimitedPayload | null) => void;
  setBlockedToast: (reason: string | null) => void;
  setBan: (payload: UserBannedPayload | null) => void;
  setReplyTarget: (msg: ChatMessage | null) => void;
  applyReaction: (messageId: string, reactions: Record<string, string[]>) => void;
  setConn: (s: ConnState, error?: string | null) => void;
  reset: () => void;
}

const TYPING_TTL_MS = 5_000;

export const useChatStore = create<ChatState>((set, get) => ({
  identity: null,
  messages: [],
  online: 0,
  typing: {},
  systemEvents: [],
  connState: 'idle',
  errorMessage: null,
  eligibility: { locked: false, progress: null },
  activation: null,
  cooldown: null,
  blockedToast: null,
  ban: null,
  replyTarget: null,

  setIdentity: identity => set({ identity }),
  setHistory: items => set({ messages: items }),

  appendMessage: msg =>
    set(state => {
      let idx = state.messages.findIndex(m => m.id === msg.id);
      if (idx < 0 && msg.clientNonce) {
        idx = state.messages.findIndex(m => m.id === msg.clientNonce);
      }
      if (idx >= 0) {
        const next = state.messages.slice();
        // Preserve any reactions already applied locally if the incoming
        // (re)broadcast doesn't carry them.
        const prev = next[idx];
        next[idx] = {
          ...msg,
          clientNonce: undefined,
          reactions: msg.reactions ?? prev.reactions,
        };
        return { messages: next };
      }
      // A new message from a user implicitly means they are no longer typing.
      const typing = { ...state.typing };
      delete typing[msg.userId];
      return { messages: [...state.messages, { ...msg, clientNonce: undefined }], typing };
    }),

  reconcileAck: (clientNonce, serverId, createdAt) =>
    set(state => ({
      messages: state.messages.map(m =>
        m.id === clientNonce ? { ...m, id: serverId, createdAt, status: 'sent' } : m,
      ),
    })),

  setOnline: n => set({ online: n }),

  addTyping: (userId, name, ttlMs = TYPING_TTL_MS) =>
    set(state => ({
      typing: {
        ...state.typing,
        [userId]: { userId, name, expiresAt: Date.now() + ttlMs },
      },
    })),

  removeTyping: userId =>
    set(state => {
      if (!state.typing[userId]) return state;
      const next = { ...state.typing };
      delete next[userId];
      return { typing: next };
    }),

  pruneTyping: () => {
    const now = Date.now();
    const current = get().typing;
    const stale = Object.values(current).filter(t => t.expiresAt <= now);
    if (stale.length === 0) return;
    set(state => {
      const next = { ...state.typing };
      for (const t of stale) delete next[t.userId];
      return { typing: next };
    });
  },

  pushSystemEvent: ev =>
    set(state => {
      const at = ev.at ?? new Date().toISOString();
      const id = `sys-${ev.kind}-${ev.userId}-${at}`;
      // Coalesce: if the most recent event is the SAME kind for the SAME user
      // within 2 seconds, drop the new one (handles flicker on quick reconnect).
      const last = state.systemEvents[state.systemEvents.length - 1];
      if (last && last.kind === ev.kind && last.userId === ev.userId &&
          Math.abs(new Date(last.at).getTime() - new Date(at).getTime()) < 2000) {
        return state;
      }
      // Cap to last 50 events to avoid unbounded growth.
      const next = [...state.systemEvents, { id, kind: ev.kind, userId: ev.userId, name: ev.name, at }];
      if (next.length > 50) next.splice(0, next.length - 50);
      return { systemEvents: next };
    }),

  setEligibility: (locked, progress) => set({ eligibility: { locked, progress } }),

  showActivation: payload => set({ activation: payload }),
  dismissActivation: () => set({ activation: null }),

  setCooldown: payload =>
    set(payload === null
      ? { cooldown: null }
      : { cooldown: { untilAt: payload.untilAt, reason: payload.reason, message: payload.message } }),

  setBlockedToast: reason =>
    set(reason === null ? { blockedToast: null } : { blockedToast: { reason, at: Date.now() } }),

  setBan: payload => set({ ban: payload }),

  setReplyTarget: msg => set({ replyTarget: msg }),

  applyReaction: (messageId, reactions) =>
    set(state => ({
      messages: state.messages.map(m =>
        m.id === messageId
          ? { ...m, reactions: Object.keys(reactions).length > 0 ? reactions : undefined }
          : m,
      ),
    })),

  setConn: (s, error = null) => set({ connState: s, errorMessage: error }),
  reset: () => set({
    identity: null,
    messages: [],
    online: 0,
    typing: {},
    systemEvents: [],
    connState: 'idle',
    errorMessage: null,
    eligibility: { locked: false, progress: null },
    activation: null,
    cooldown: null,
    blockedToast: null,
    ban: null,
    replyTarget: null,
  }),
}));

// Keep the typing map clean even if a typing:user-stopped is missed.
if (typeof window !== 'undefined') {
  setInterval(() => useChatStore.getState().pruneTyping(), 1000);
}
